import React, { useState } from "react";
import { useQuery } from "react-query";
import { Button, Input, Skeleton, Typography } from "antd";
import { CheckCircleFilled, EyeOutlined } from "@ant-design/icons";
import * as TemplateApi from "../../services/projectTemplates";
import { toFa } from "../../utils/helpers";
import TemplatePreviewModal from "./TemplatePreviewModal";
import styles from "./styles.module.css";

/* Second step of setup: one question, and a real project at the end of it.
 *
 * The starters are the organization's own project templates — it was given a
 * copy of the ones matching its industry when it was created — so there is no
 * second list to keep in step, and nothing here that belongs to a different
 * kind of business. */
const ProjectStep = ({ loading, onSubmit, onSkip }) => {
  const [title, setTitle] = useState("");
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);

  const templatesQuery = useQuery(
    ["onboarding-templates"],
    () =>
      TemplateApi.getProjectTemplates({ params: { per_page: 50 } }).then(
        (result) => result?.data?.templates || [],
      ),
    { staleTime: 5 * 60 * 1000 },
  );

  const templates = templatesQuery.data || [];
  const canSubmit = Boolean(title.trim());

  return (
    <>
      <div className={styles.stepHead}>
        <Typography.Title level={4} className={styles.stepTitle}>
          الان روی چی کار می‌کنید؟
        </Typography.Title>
        <p className={styles.stepDescription}>
          اسم اولین پروژه‌ات رو بنویس. اگه بخوای می‌تونی از یکی از قالب‌های آماده
          شروع کنی تا ستون‌ها و چند تسک اولیه از قبل چیده باشن.
        </p>
      </div>

      <Input
        size="large"
        autoFocus
        value={title}
        maxLength={120}
        placeholder="مثلاً بازطراحی سایت شرکت"
        onChange={(event) => setTitle(event.target.value)}
        onPressEnter={() => canSubmit && onSubmit({ title: title.trim(), template: selected })}
      />

      <div className={styles.starterHead}>
        <span>از یک قالب آماده شروع کن</span>
        <span className={styles.starterOptional}>اختیاری</span>
      </div>

      {templatesQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <div className={styles.starterGrid}>
          {templates.map((template) => {
            const isSelected = selected?.id === template.id;
            const stepCount = template?.steps?.length || 0;
            const taskCount =
              (template?.tasks?.length || 0) +
              (template?.steps || []).reduce(
                (sum, step) => sum + (step?.tasks?.length || 0),
                0,
              );

            /* A div rather than a button: the preview control is itself a
             * button, and a button inside a button is invalid markup that
             * browsers recover from by dropping the inner one. Keyboard access
             * is kept explicitly instead. */
            return (
              <div
                key={template.id}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                className={`${styles.starterCard} ${isSelected ? styles.starterCardActive : ""}`}
                onClick={() => setSelected(isSelected ? null : template)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  setSelected(isSelected ? null : template);
                }}
              >
                {isSelected ? (
                  <CheckCircleFilled className={styles.starterCheck} />
                ) : null}
                <span className={styles.starterTitle}>{template.title}</span>
                <span className={styles.starterMeta}>
                  {toFa(stepCount)} ستون · {toFa(taskCount)} تسک
                </span>
                <button
                  type="button"
                  className={styles.starterPreview}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPreview(template);
                  }}
                >
                  <EyeOutlined /> پیش‌نمایش
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.stepActions}>
        <Button
          type="primary"
          size="large"
          block
          loading={loading}
          disabled={!canSubmit}
          onClick={() => onSubmit({ title: title.trim(), template: selected })}
        >
          ساخت پروژه و ادامه
        </Button>
        <Button type="text" className={styles.skipButton} onClick={onSkip}>
          فعلاً پروژه نمی‌سازم
        </Button>
      </div>

      <TemplatePreviewModal
        template={preview}
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        onSelect={(template) => {
          setSelected(template);
          setPreview(null);
        }}
      />
    </>
  );
};

export default ProjectStep;
